import React from "react";
import { Trans } from "@lingui/macro";
import { X, Upload, Check, File as FileIcon } from "lucide-react";
import { SendFile } from "../../../models/Message";
import { Util } from "../../../services/Utils/Util";
import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/progress";

interface FileUploadFabProps {
    roomUploadFiles: SendFile[];
    showUploadFab: boolean;
    setShowUploadFab: (show: boolean) => void;
    sendFileProgress: number;
    cancelUpload: (file: SendFile) => void;
    closeUploadFab: () => void;
}

export const FileUploadFab: React.FC<FileUploadFabProps> = ({
    roomUploadFiles, showUploadFab, setShowUploadFab, sendFileProgress, cancelUpload, closeUploadFab
}) => {
    if (roomUploadFiles.length === 0) return null;

    return (
        <div className="absolute bottom-6 right-6 z-50">
            {showUploadFab ? (
                <div className="bg-background rounded-xl shadow-xl w-80 border border-border overflow-hidden">
                    <div className="px-4 py-3 bg-muted/50 border-b border-border flex items-center justify-between">
                        <span className="font-medium text-sm">
                            {roomUploadFiles.filter(f => f.status == "done").length} / {roomUploadFiles.length} <Trans>Uploaded</Trans> {sendFileProgress}%
                        </span>
                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={closeUploadFab}><X className="w-4 h-4" /></Button>
                    </div>
                    <div className="max-h-60 overflow-y-auto p-2">
                        {roomUploadFiles.map(file => (
                            <div key={file.id} className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded-lg">
                                <div className="w-10 h-10 rounded bg-muted flex items-center justify-center shrink-0 overflow-hidden">
                                    {file.file.type.includes('image') ? (
                                        <img src={URL.createObjectURL(file.file)} className="w-full h-full object-cover" alt={file.file.name} />
                                    ) : (
                                        <FileIcon className="w-5 h-5 text-muted-foreground" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{file.file.name}</div>
                                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                                        <span>{Util.niceBytes(file.file.size)}</span>
                                        {file.status === 'done' && <Check className="w-3 h-3 text-green-500" />}
                                        {file.status === 'canceled' && <span className="text-destructive"><Trans>Canceled</Trans></span>}
                                    </div>
                                    {file.status === 'uploading' && <Progress value={file.progress * 100} className="h-1 mt-1" />}
                                </div>
                                {file.status !== 'done' && file.status !== 'canceled' && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => cancelUpload(file)}>
                                        <X className="w-4 h-4" />
                                    </Button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <Button
                    className="h-14 w-14 rounded-full bg-primary hover:bg-primary/90 shadow-lg flex flex-col items-center justify-center p-0"
                    onClick={() => setShowUploadFab(true)}
                >
                    <span className="text-meta-sm font-bold">{sendFileProgress}%</span>
                    <Upload className="w-5 h-5" />
                </Button>
            )}
        </div>
    );
};
